import React from "react";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import { TwicpicsProvider } from "./contexts/Twicpics";
import { PackagesProps } from "src/components/packages";

interface RouterProps {
  Homepage: React.FC<RouteComponentProps<any>>;
  SignIn: React.FC<RouteComponentProps<any>>;
  StartPasswordReset: React.FC<RouteComponentProps<any>>;
  ConfirmPasswordReset: React.FC<RouteComponentProps<any>>;
  Blog: React.FC<RouteComponentProps<any>>;
  About: React.FC<RouteComponentProps<any>>;
  ForTalent: React.FC<RouteComponentProps<any>>;
  ForClients: React.FC<RouteComponentProps<any>>;
  ForAgencies: React.FC<RouteComponentProps<any>>;
  Careers: React.FC<RouteComponentProps<any>>;
  Podcast: React.FC<RouteComponentProps<any>>;
  Account: React.FC<RouteComponentProps<any>>;
  CreateAccount: React.FC<RouteComponentProps<any>>;
  AddWorkspace: React.FC<RouteComponentProps<any>>;
  CreateJob: React.FC<RouteComponentProps<any>>;
  Dashboard: React.FC<RouteComponentProps<any>>;
  DeskRental: React.FC<RouteComponentProps<any>>;
  SelfManagedTalentOnboarding: React.FC<RouteComponentProps<any>>;
  TalentOnboarding: React.FC<RouteComponentProps<any>>;
  AgentOnboarding: React.FC<RouteComponentProps<any>>;
  JoinBookingCompany: React.FC<RouteComponentProps<any>>;
  PaymentVerification: React.FC<RouteComponentProps<any>>;
  Packages: React.FC<PackagesProps>;
  BuilderLandingPage: React.FC<RouteComponentProps<any>>;
  TalentProfile: React.FC<RouteComponentProps<any>>;
  Static: React.FC<RouteComponentProps<any>>;
  AgencyDirectory: React.FC<RouteComponentProps<any>>;
  AgencyHomepage: React.FC<RouteComponentProps<any>>;
  NotFound: React.FC<{ message?: React.ReactNode }>;
  EmailVerification: React.FC<RouteComponentProps<any>>;
  CreateAccountOnboarding: React.FC<RouteComponentProps<any>>;
  TalentPortfolioOnboarding: React.FC<RouteComponentProps<any>>;
  AccountNotFound: React.FC<RouteComponentProps<any>>;
  VerifyEmail: React.FC<RouteComponentProps<any>>;
  OpenCalls: React.FC<RouteComponentProps<any>>;
  OpenCall: React.FC<RouteComponentProps<any>>;
}

export const Router: React.FC<RouterProps> = ({
  Homepage,
  SignIn,
  StartPasswordReset,
  ConfirmPasswordReset,
  Blog,
  About,
  ForTalent,
  ForClients,
  ForAgencies,
  Careers,
  Podcast,
  Account,
  CreateAccount,
  AddWorkspace,
  CreateJob,
  Dashboard,
  DeskRental,
  PaymentVerification,
  SelfManagedTalentOnboarding,
  TalentOnboarding,
  AgentOnboarding,
  JoinBookingCompany,
  Packages,
  BuilderLandingPage,
  TalentProfile,
  Static,
  AgencyDirectory,
  AgencyHomepage,
  NotFound,
  EmailVerification,
  CreateAccountOnboarding,
  TalentPortfolioOnboarding,
  AccountNotFound,
  VerifyEmail,
  OpenCalls,
  OpenCall,
}) => {
  return (
    <Switch>
      <Route component={AddWorkspace} path="/account/add-workspace" exact />
      <Route component={Homepage} path="/" exact />
      <Route component={SignIn} path="/sign-in" />
      <Redirect from="/login" to="/sign-in" />
      <Route
        exact
        path="/reset-password"
        render={(props) => (
          <TwicpicsProvider>
            <StartPasswordReset
              location={props.location}
              history={props.history}
              match={props.match}
            />
          </TwicpicsProvider>
        )}
      />
      <Route
        path="/reset-password/:token"
        render={(props) => (
          <TwicpicsProvider>
            <ConfirmPasswordReset
              location={props.location}
              history={props.history}
              match={props.match}
            />
          </TwicpicsProvider>
        )}
      />
      <Route component={Blog} path="/backstage" />
      <Route component={About} path="/about" />
      <Route component={ForTalent} path="/for/talent" />
      <Route component={ForTalent} path="/for/creatives" />
      <Route component={ForClients} path="/for/clients" />
      <Route component={ForAgencies} path="/for/agencies" />
      <Redirect from="/new" to="/" />
      <Route component={Careers} path="/careers" />
      <Route component={Podcast} path="/podcast" />
      <Route component={Podcast} path="/creative-paths" />
      <Route component={AccountNotFound} path="/account/not-found" />
      <Route component={EmailVerification} path="/account/confirm-email" />
      {/* The portfolio step is its own resumable route (KTD1). The onboarding
          page is exact so nothing beneath it falls through to its mount
          effect, which pushes any user with a kind to /dashboard. */}
      <Route
        component={TalentPortfolioOnboarding}
        path="/account/onboarding/portfolio"
      />
      <Route
        component={CreateAccountOnboarding}
        path="/account/onboarding"
        exact
      />
      <Route
        path="/account/new"
        render={(props) => (
          <TwicpicsProvider>
            <CreateAccount
              location={props.location}
              history={props.history}
              match={props.match}
            />
          </TwicpicsProvider>
        )}
      />

      <Route component={Account} path="/account" />
      <Route component={CreateJob} path="/jobs/new" />
      <Route component={Dashboard} path="/dashboard" />
      <Route component={DeskRental} path="/desk-rental" exact />
      <Route
        path="/talent-onboarding/:token"
        render={(props) => (
          <TwicpicsProvider>
            <TalentOnboarding
              location={props.location}
              history={props.history}
              match={props.match}
            />
          </TwicpicsProvider>
        )}
      />
      <Route
        path="/create-profile/:token"
        render={(props) => (
          <TwicpicsProvider>
            <SelfManagedTalentOnboarding
              location={props.location}
              history={props.history}
              match={props.match}
            />
          </TwicpicsProvider>
        )}
      />
      <Route
        path="/agent-onboarding"
        render={(props) => (
          <TwicpicsProvider>
            <AgentOnboarding
              location={props.location}
              history={props.history}
              match={props.match}
            />
          </TwicpicsProvider>
        )}
      />
      <Route
        path="/join-booking-team/:token"
        render={(props) => (
          <TwicpicsProvider>
            <JoinBookingCompany
              location={props.location}
              history={props.history}
              match={props.match}
            />
          </TwicpicsProvider>
        )}
      />
      <Redirect from="/models/:slug" to="/creatives/:slug" />
      <Route component={Packages} path="/packages/:slug" />
      <Route component={AgencyDirectory} path="/agencies" exact />
      <Route
        path="/agency/:slug"
        exact
        render={(props) => (
          <TwicpicsProvider>
            <AgencyHomepage
              location={props.location}
              history={props.history}
              match={props.match}
            />
          </TwicpicsProvider>
        )}
      />
      <Route
        component={PaymentVerification}
        path="/payment-verification/:token"
      />
      <Route component={VerifyEmail} path="/verify-email/:token" />
      <Route component={OpenCalls} path="/open-calls" exact />
      <Route component={OpenCall} path="/open-calls/:slug" />
      <Route component={BuilderLandingPage} path="/l/:slug" />
      <Route
        component={TalentProfile}
        path="/creatives/:slug/:talentProfileId?"
      />
      <Route component={Static} path="/:slug" />
      <Route component={NotFound} path="*" />
    </Switch>
  );
};
